import { useLanguage } from '../../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiChevronLeft, FiPackage, FiMapPin, FiCreditCard, FiXCircle, FiRotateCcw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import orderService from '../../services/order.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import OrderTimeline from '../../components/order/OrderTimeline';
import ContactSellerButton from '../../components/chat/ContactSellerButton';

const OrderDetailPage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await orderService.getById(id);
        setOrder(data.data);
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) return <LoadingSpinner />;

  if (!order) {
    return (
      <div className="section-padding text-center">
        <h3>{t('orderNotFound')}</h3>
        <Link to="/account/orders" className="btn btn-fd-primary mt-3">{t('backToOrders')}</Link>
      </div>
    );
  }

  const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
  const shippingAddress = typeof order.shippingAddress === 'string'
    ? JSON.parse(order.shippingAddress) : order.shippingAddress;

  const isCancelled = order.status === 'cancelled' || order.status === 'refunded';
  const canCancel = ['pending', 'confirmed', 'processing'].includes(order.status);
  const canReturn = order.status === 'delivered';

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await orderService.cancelOrder(order.id, cancelReason);
      toast.success(t('orderCancelledSuccess'));
      setShowCancelModal(false);
      setOrder({ ...order, status: 'cancelled' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel order');
    } finally { setActionLoading(false); }
  };

  const handleReturn = async () => {
    if (!returnReason.trim()) { toast.warning(t('pleaseProvideReason')); return; }
    setActionLoading(true);
    try {
      await orderService.requestReturn(order.id, { reason: returnReason });
      toast.success(t('returnRequestSubmitted'));
      setShowReturnModal(false);
      setOrder({ ...order, status: 'return_requested' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit return request');
    } finally { setActionLoading(false); }
  };

  return (
    <div className="section-padding">
      <div className="container fd-page-container" style={{ maxWidth: '900px' }}>
        {/* Header */}
        <Link to="/account/orders" className="text-decoration-none text-muted d-inline-flex align-items-center gap-1 mb-3">
          <FiChevronLeft /> Back to Orders
        </Link>

        <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
          <div>
            <h2 className="mb-1">Order {order.orderNumber}</h2>
            <p className="text-muted mb-0">
              Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              })}
            </p>
          </div>
          <div className="text-end">
            <span className={`badge bg-${isCancelled ? 'danger' : 'primary'} fs-6`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
            <div className="mt-2">
              <a
                href={`${import.meta.env.VITE_API_URL || '/api'}/v1/orders/${order.id}/invoice`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-fd-outline"
              >
                📄 Invoice
              </a>
              {canCancel && (
                <button className="btn btn-sm btn-outline-danger ms-2" onClick={() => setShowCancelModal(true)}>
                  <FiXCircle className="me-1" /> {t('cancelOrder')}
                </button>
              )}
              {canReturn && (
                <button className="btn btn-sm btn-outline-warning ms-2" onClick={() => setShowReturnModal(true)}>
                  <FiRotateCcw className="me-1" /> {t('requestReturn')}
                </button>
              )}
              {order.items?.[0]?.merchantId && (
                <div className="mt-2">
                  <ContactSellerButton
                    merchantId={order.items[0].merchantId}
                    orderId={order.id}
                    variant="link"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Tracking Timeline */}
        <div className="dashboard-card mb-4">
          <OrderTimeline order={order} />
          {order.trackingNumber && order.shippingCarrier && (
            <div className="mt-3 p-3" style={{ background: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#059669', marginBottom: '4px' }}>
                🚚 Tracking Information
              </div>
              <div style={{ fontSize: '13px' }}>
                <strong>{t('carrier')}:</strong> {order.shippingCarrier} &nbsp;·&nbsp;
                <strong>{t('trackingNumber')}:</strong> {order.trackingNumber}
              </div>
              <a
                href={
                  order.shippingCarrier?.toLowerCase().includes('fedex') ? `https://www.fedex.com/fedextrack/?trknbr=${order.trackingNumber}` :
                  order.shippingCarrier?.toLowerCase().includes('ups') ? `https://www.ups.com/track?tracknum=${order.trackingNumber}` :
                  `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.trackingNumber}`
                }
                target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-outline-success mt-2"
                style={{ fontSize: '12px' }}
              >
                📍 Track Package
              </a>
            </div>
          )}
        </div>

        <div className="row g-4">
          {/* Order Items */}
          <div className="col-lg-7">
            <div className="dashboard-card">
              <h5 className="mb-3">Items ({items?.length || 0})</h5>
              {items?.map((item, index) => (
                <div key={index} className={`d-flex gap-3 py-3 ${index > 0 ? 'border-top' : ''}`}>
                  <img
                    src={item.image || '/placeholder.png'}
                    alt={item.name}
                    className="rounded"
                    style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                  />
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{item.name}</h6>
                    <small className="text-muted">Qty: {item.quantity} × ${parseFloat(item.price).toFixed(2)}</small>
                  </div>
                  <strong>${(item.quantity * parseFloat(item.price)).toFixed(2)}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-5">
            {/* Payment Summary */}
            <div className="dashboard-card mb-3">
              <h5 className="mb-3">{t('paymentSummary')}</h5>
              <div className="d-flex justify-content-between mb-2">
                <span>{t('subtotal')}</span>
                <span>${parseFloat(order.subtotal).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>{t('tax')}</span>
                <span>${parseFloat(order.tax).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>{t('shipping')}</span>
                <span>
                  {parseFloat(order.shippingCost) === 0
                    ? <span className="text-success">{t('free')}</span>
                    : `$${parseFloat(order.shippingCost).toFixed(2)}`}
                </span>
              </div>
              {parseFloat(order.discount) > 0 && (
                <div className="d-flex justify-content-between mb-2 text-success">
                  <span>{t('discount')}</span>
                  <span>-${parseFloat(order.discount).toFixed(2)}</span>
                </div>
              )}
              <hr />
              <div className="d-flex justify-content-between">
                <strong>{t('total')}</strong>
                <strong className="text-primary fs-5">${parseFloat(order.totalAmount).toFixed(2)}</strong>
              </div>

              <div className="mt-3 pt-3 border-top">
                <div className="d-flex align-items-center gap-2">
                  <FiCreditCard className="text-muted" />
                  <span className="text-muted small">
                    Payment: <span className={`badge bg-${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
                      {order.paymentStatus}
                    </span>
                  </span>
                </div>
                {order.paymentMethod && (
                  <small className="text-muted">Method: {order.paymentMethod}</small>
                )}
                {order.paidAt && (
                  <small className="text-muted d-block">
                    Paid on {new Date(order.paidAt).toLocaleDateString()}
                  </small>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            {shippingAddress && (
              <div className="dashboard-card">
                <h5 className="mb-3">
                  <FiMapPin className="me-2" />Shipping Address
                </h5>
                <p className="mb-0">
                  {shippingAddress.fullName && <>{shippingAddress.fullName}<br /></>}
                  {shippingAddress.street}<br />
                  {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}<br />
                  {shippingAddress.country}
                </p>
                {shippingAddress.phone && (
                  <p className="mb-0 mt-2 text-muted small">Phone: {shippingAddress.phone}</p>
                )}
              </div>
            )}

            {order.notes && (
              <div className="dashboard-card mt-3">
                <h6>{t('orderNotes')}</h6>
                <p className="text-muted mb-0">{order.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '32px', maxWidth: '480px', width: '90%' }}>
            <h4 style={{ color: '#DC2626', marginBottom: '16px' }}><FiXCircle className="me-2" />{t('cancelOrder')}</h4>
            <p style={{ color: '#6B7280' }}>{t('cancelOrderConfirm')}</p>
            <textarea
              className="form-control mb-3"
              rows={3}
              placeholder={t('cancelReasonPlaceholder')}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>{t('cancel')}</button>
              <button className="btn btn-danger" disabled={actionLoading} onClick={handleCancel}>
                {actionLoading ? t('cancelling') : t('confirmCancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Request Modal */}
      {showReturnModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '32px', maxWidth: '480px', width: '90%' }}>
            <h4 style={{ color: '#D97706', marginBottom: '16px' }}><FiRotateCcw className="me-2" />{t('requestReturn')}</h4>
            <p style={{ color: '#6B7280' }}>{t('returnConfirm')}</p>
            <textarea
              className="form-control mb-3"
              rows={3}
              placeholder={t('returnReasonPlaceholder')}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              required
            />
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary" onClick={() => setShowReturnModal(false)}>{t('cancel')}</button>
              <button className="btn btn-warning" disabled={actionLoading} onClick={handleReturn}>
                {actionLoading ? t('submitting') : t('submitReturn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
